import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Truck, Box, Weight, Ruler, Leaf, ArrowRight, CheckCircle } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { useSiteContent } from '../context/SiteContentContext';
import { usePageTitle } from '../hooks/usePageTitle';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TRUCK_IMAGES = {
  hero: '/images/truck-hero.jpg',
  snow: '/images/truck-snow.jpg',
  port: '/images/truck-port.jpg'
};

// Fallback images for fleet vehicles without custom images
const defaultFleetImages = { 'sprinter': TRUCK_IMAGES.hero, 'atego': TRUCK_IMAGES.snow, 'actros-solo': TRUCK_IMAGES.port, 'jumbo-trailer': TRUCK_IMAGES.hero };

// Helper to get vehicle image (database image_url or fallback)
const getVehicleImage = (vehicle) => {
  if (vehicle.image_url && vehicle.image_url.trim() !== '') {
    return vehicle.image_url;
  }
  return defaultFleetImages[vehicle.id] || TRUCK_IMAGES.hero;
};

export default function FleetPage() {
  const [fleet, setFleet] = useState([]);
  const [pageContent, setPageContent] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const { language, localizedPath } = useSiteContent();
  
  // Set page title
  usePageTitle('fleet');

  // Default translations
  const defaultContent = language === 'bg' ? {
    heroSubtitle: 'НАШИЯТ ФЛОТ',
    heroTitle: 'Модерен',
    heroTitleHighlight: 'EURO 6',
    heroTitleEnd: 'флот',
    heroDescription: 'Нашият разнообразен флот покрива обеми на товари от 11 m³ до 121 m³, оборудван с най-новите технологии и отговарящ на най-високите екологични стандарти.',
    features: [
      'EURO 6 емисионни стандарти на всички превозни средства',
      'ECO зона достъп сертификат за всички западноевропейски градове',
      'Хидравлични платформи и електрокари за гъвкаво товарене',
      'GPS проследяване на всяко превозно средство',
      'Редовна поддръжка и проверки за безопасност',
      'Професионално обучени шофьори'
    ],
    gridSubtitle: 'ГАМА ПРЕВОЗНИ СРЕДСТВА',
    gridTitle: 'Изберете правилното',
    gridTitleHighlight: 'превозно средство',
    capacity: 'Капацитет',
    payload: 'Товароносимост',
    euroPallets: 'Евро палети',
    dimensions: 'Размери',
    ecoZoneCertified: 'ECO зона достъп сертифициран',
    ctaTitle: 'Не сте сигурни кое превозно средство ви трябва?',
    ctaText: 'Нашите логистични експерти ще ви помогнат да изберете перфектното решение за вашия товар.',
    ctaButton: 'Получете експертен съвет'
  } : {
    heroSubtitle: 'OUR FLEET',
    heroTitle: 'Modern',
    heroTitleHighlight: 'EURO 6',
    heroTitleEnd: 'Fleet',
    heroDescription: 'Our diverse fleet covers cargo volumes from 11 m³ to 121 m³, equipped with the latest technology and meeting the highest environmental standards.',
    features: [
      'EURO 6 emission standards on all vehicles',
      'ECO zone access certification for all Western European cities',
      'Tail lifts and pump trucks for flexible loading',
      'GPS tracking on every vehicle',
      'Regular maintenance and safety inspections',
      'Professional trained drivers'
    ],
    gridSubtitle: 'VEHICLE RANGE',
    gridTitle: 'Choose the Right',
    gridTitleHighlight: 'Vehicle',
    capacity: 'Capacity',
    payload: 'Payload',
    euroPallets: 'Euro Pallets',
    dimensions: 'Dimensions',
    ecoZoneCertified: 'ECO Zone Access Certified',
    ctaTitle: 'Not Sure Which Vehicle You Need?',
    ctaText: 'Our logistics experts will help you choose the perfect solution for your cargo.',
    ctaButton: 'Get Expert Advice'
  };

  // Merge database content with defaults
  const content = {
    heroSubtitle: (language === 'bg' ? pageContent?.hero_subtitle_bg : pageContent?.hero_subtitle_en) || defaultContent.heroSubtitle,
    heroTitle: (language === 'bg' ? pageContent?.hero_title_bg : pageContent?.hero_title_en) || defaultContent.heroTitle,
    heroTitleHighlight: (language === 'bg' ? pageContent?.hero_title_highlight_bg : pageContent?.hero_title_highlight_en) || defaultContent.heroTitleHighlight,
    heroTitleEnd: (language === 'bg' ? pageContent?.hero_title_end_bg : pageContent?.hero_title_end_en) || defaultContent.heroTitleEnd,
    heroDescription: (language === 'bg' ? pageContent?.hero_description_bg : pageContent?.hero_description_en) || defaultContent.heroDescription,
    features: (language === 'bg' ? pageContent?.features_bg : pageContent?.features_en) || defaultContent.features,
    gridSubtitle: (language === 'bg' ? pageContent?.grid_subtitle_bg : pageContent?.grid_subtitle_en) || defaultContent.gridSubtitle,
    gridTitle: (language === 'bg' ? pageContent?.grid_title_bg : pageContent?.grid_title_en) || defaultContent.gridTitle,
    gridTitleHighlight: (language === 'bg' ? pageContent?.grid_title_highlight_bg : pageContent?.grid_title_highlight_en) || defaultContent.gridTitleHighlight,
    capacity: defaultContent.capacity,
    payload: defaultContent.payload,
    euroPallets: defaultContent.euroPallets,
    dimensions: defaultContent.dimensions,
    ecoZoneCertified: defaultContent.ecoZoneCertified,
    ctaTitle: (language === 'bg' ? pageContent?.cta_title_bg : pageContent?.cta_title_en) || defaultContent.ctaTitle,
    ctaText: (language === 'bg' ? pageContent?.cta_text_bg : pageContent?.cta_text_en) || defaultContent.ctaText,
    ctaButton: (language === 'bg' ? pageContent?.cta_button_bg : pageContent?.cta_button_en) || defaultContent.ctaButton
  };

  useEffect(() => {
    const fetchData = async () => { 
      try { 
        const [fleetRes, contentRes] = await Promise.all([
          axios.get(`${API}/fleet`),
          axios.get(`${API}/fleet-page-content`)
        ]);
        setFleet(fleetRes.data.fleet);
        if (contentRes.data && Object.keys(contentRes.data).length > 0) {
          setPageContent(contentRes.data);
        }
      } catch (error) { 
        console.error('Error:', error); 
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, []);

  // Show loader while data is loading
  if (isLoading) {
    return (
      <div className="page-loader">
        <div className="page-loader-spinner"></div>
      </div>
    );
  }

  return (
    <div data-testid="fleet-page" className="pt-20 bg-slate-50">
      {/* Hero */}
      <section className="relative py-24 md:py-32" style={{ backgroundImage: `url(${TRUCK_IMAGES.snow})`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
        <div className="absolute inset-0 bg-gradient-to-r from-white/95 via-white/85 to-white/60" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8">
          <span className="overline mb-4 block">{content.heroSubtitle}</span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-slate-900 mb-6">
            {content.heroTitle} <span className="text-[#C41E3A]">{content.heroTitleHighlight}</span> {content.heroTitleEnd}
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl">{content.heroDescription}</p>
        </div>
      </section>

      {/* Features */}
      <section className="py-16 bg-white border-y border-slate-100">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {content.features.map((feature, index) => (
              <div key={index} className="flex items-center gap-3 animate-fade-in-up" style={{ animationDelay: `${index * 0.1}s` }}>
                <CheckCircle className="w-5 h-5 text-[#C41E3A] flex-shrink-0" />
                <span className="text-slate-600">{feature}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Fleet Grid */}
      <section data-testid="fleet-grid" className="section-padding bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-16">
            <span className="overline mb-4 block">{content.gridSubtitle}</span>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900">
              {content.gridTitle} <span className="text-[#C41E3A]">{content.gridTitleHighlight}</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {fleet.map((vehicle, index) => {
              const vehicleType = language === 'bg' && vehicle.type_bg ? vehicle.type_bg : (vehicle.type_en || vehicle.type);
              const vehicleName = language === 'bg' && vehicle.name_bg ? vehicle.name_bg : (vehicle.name_en || vehicle.name);
              return (
                <Card key={vehicle.id} data-testid={`fleet-card-${vehicle.id}`} className="bg-white border-slate-100 overflow-hidden hover:shadow-xl transition-all duration-300 rounded-3xl animate-fade-in-up" style={{ animationDelay: `${index * 0.1}s` }}>
                  <div className="aspect-[16/9] overflow-hidden">
                    <img src={getVehicleImage(vehicle)} alt={vehicleName} className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
                  </div>
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <h3 className="text-xl font-bold text-slate-900">{vehicleName}</h3>
                        <span className="text-sm text-slate-500">{vehicleType}</span>
                      </div>
                      <div className="px-3 py-1 bg-red-50 rounded-full text-[#C41E3A] text-xs font-bold">{vehicle.eco_standard}</div>
                    </div>
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div className="flex items-center gap-2">
                        <Box className="w-4 h-4 text-[#C41E3A]" />
                        <div>
                          <div className="text-slate-900 font-bold">{vehicle.capacity_m3} m³</div>
                          <div className="text-xs text-slate-400">{content.capacity}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Weight className="w-4 h-4 text-[#C41E3A]" />
                        <div>
                          <div className="text-slate-900 font-bold">{vehicle.payload_kg} kg</div>
                          <div className="text-xs text-slate-400">{content.payload}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Truck className="w-4 h-4 text-[#C41E3A]" />
                        <div>
                          <div className="text-slate-900 font-bold">{vehicle.euro_pallets}</div>
                          <div className="text-xs text-slate-400">{content.euroPallets}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Ruler className="w-4 h-4 text-[#C41E3A]" />
                        <div>
                          <div className="text-slate-900 font-bold text-sm">{vehicle.dimensions}</div>
                          <div className="text-xs text-slate-400">{content.dimensions}</div>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-slate-500">
                      <Leaf className="w-4 h-4 text-green-500" />
                      <span>{content.ecoZoneCertified}</span>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-r from-[#C41E3A] to-[#A01830]">
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">{content.ctaTitle}</h2>
          <p className="text-white/80 mb-8">{content.ctaText}</p>
          <Link to={localizedPath('/contact')}>
            <button data-testid="fleet-contact-btn" className="btn-modern btn-light btn-lg">
              {content.ctaButton} <ArrowRight className="w-4 h-4" />
            </button>
          </Link>
        </div>
      </section>
    </div>
  );
}
